// ============================================================================
// GERMANY TAX CALCULATION - Einkommensteuer 2025
// Source: Bundesfinanzministerium, lohn-info.de
// Official data from Sozialversicherungsrechengrößenverordnung 2025
// ============================================================================

import { TaxCalculationResult, CountryTaxData, DeductionBreakdown } from "./types";

/**
 * GERMANY TAX CONFIG 2025
 * 
 * Einkommensteuer 2025:
 * - Grundfreibetrag: 12.096€ (up from 11.604€)
 * - Progressive zone 1: 12.096€ - 17.443€ (14% starting)
 * - Progressive zone 2: 17.443€ - 68.429€ (14% - 42% progressive)
 * - Proportional zone 1: 68.430€ - 277.825€ (42%)
 * - Proportional zone 2: 277.826€+ (45% - Reichensteuer)
 * 
 * Sozialversicherung 2025:
 * - Beitragsbemessungsgrenze Rente/Arbeitslos: 96.600€/Jahr (8.050€/Monat)
 * - Beitragsbemessungsgrenze Kranken/Pflege: 66.150€/Jahr (5.512,50€/Monat)
 * - Total employee contribution: ~21.1% (varies by health insurance)
 */
export const GERMANY_TAX_CONFIG: CountryTaxData = {
  // Employee share 2025: Pension 9.3% + Health 8.55% + Unemployment 1.3% + Care ~2.05%
  socialSecurityRate: 21.2,
  socialSecurityMaxBase: 96600, // Beitragsbemessungsgrenze Rente 2025
  taxBrackets: [
    { min: 0, max: 12096, rate: 0 }, // Grundfreibetrag 2025
    { min: 12096, max: 17443, rate: 14 }, // Progressive zone 1 (starting rate)
    { min: 17443, max: 68429, rate: 24 }, // Progressive zone 2 (avg ~24%)
    { min: 68429, max: 277825, rate: 42 }, // Proportional zone 1
    { min: 277825, max: Infinity, rate: 45 }, // Reichensteuer
  ],
  personalAllowance: 12096, // Grundfreibetrag 2025
  marriedAllowance: 24192, // Double for married (Ehegattensplitting)
  childAllowances: [6612, 6612, 6612], // Kinderfreibetrag 2025 (3.306€ x 2 parents)
  currency: "EUR",
};

/**
 * Social Security contribution ceilings 2025
 * Source: Sozialversicherungsrechengrößen 2025
 * Note: From 2025, unified ceilings for East and West Germany
 */
const SS_CEILINGS_2025 = {
  pension: 96600, // Beitragsbemessungsgrenze Rente/Arbeitslos (8.050€/month)
  health: 66150, // Beitragsbemessungsgrenze Kranken/Pflege (5.512,50€/month)
};

/**
 * Employee contribution rates 2025
 * Source: lohn-info.de/sozialversicherungsbeitraege2025.html
 * 
 * Total rates (employer + employee):
 * - Rentenversicherung: 18.6% (employee: 9.3%)
 * - Arbeitslosenversicherung: 2.6% (employee: 1.3%)
 * - Krankenversicherung: 14.6% + 2.5% Zusatzbeitrag = 17.1% (employee: 8.55%)
 * - Pflegeversicherung: 3.6% (employee: 1.8% + 0.6% if childless over 23)
 */
const SS_RATES_2025 = {
  pension: 9.3, // Rentenversicherung (half of 18.6%)
  unemployment: 1.3, // Arbeitslosenversicherung (half of 2.6%)
  health: 7.3, // Krankenversicherung base (half of 14.6%)
  healthAdditional: 1.25, // Durchschnittlicher Zusatzbeitrag 2025 (half of 2.5%)
  care: 1.8, // Pflegeversicherung base (half of 3.6%)
  careChildless: 0.6, // Beitragszuschlag für Kinderlose (full 0.6%, only employee)
  careMultiChild: [0, 0, 0.25, 0.50, 0.75, 1.0], // Reduction for 2-5+ children
};

export interface GermanyTaxParams {
  grossIncome: number;
  familyStatus: string;
  dependents: number;
  age?: number;
  hasChurchTax?: boolean; // Kirchensteuer (8-9% of income tax)
  churchTaxRate?: number; // 8% in Bavaria/Baden-Württemberg, 9% elsewhere
}

/**
 * Calcula el Einkommensteuer (IRPF) y Sozialversicherung (SS) de Alemania 2025
 * 
 * Incluye:
 * - Einkommensteuer (tramos progresivos con Grundfreibetrag 12.096€)
 * - Solidaritätszuschlag (5.5% solo si impuesto > 19.950€ solteros / 39.900€ casados)
 * - Kirchensteuer opcional (8-9% del Einkommensteuer)
 * - Sozialversicherung (Pensión, Desempleo, Salud, Cuidados)
 * - Kinderfreibetrag o Kindergeld
 * - Ehegattensplitting para casados
 * - Reducciones por número de hijos en Pflegeversicherung
 * 
 * Sources:
 * - Bundesfinanzministerium
 * - Sozialversicherungsrechengrößenverordnung 2025
 * - lohn-info.de/sozialversicherungsbeitraege2025.html
 */
export function calculateGermanyTax({
  grossIncome,
  familyStatus,
  dependents,
  age = 30,
  hasChurchTax = false,
  churchTaxRate = 9, // Default: 9% (most federal states)
}: GermanyTaxParams): TaxCalculationResult {
  const config = GERMANY_TAX_CONFIG;

  // ============================================================================
  // SOCIAL SECURITY (Sozialversicherung) 2025
  // ============================================================================
  
  // Pension & Unemployment (up to pension ceiling: 96.600€)
  const pensionBase = Math.min(grossIncome, SS_CEILINGS_2025.pension);
  const pensionContrib = (pensionBase * SS_RATES_2025.pension) / 100;
  const unemploymentContrib = (pensionBase * SS_RATES_2025.unemployment) / 100;

  // Health & Care (up to health ceiling: 66.150€)
  const healthBase = Math.min(grossIncome, SS_CEILINGS_2025.health);
  const healthContrib = (healthBase * (SS_RATES_2025.health + SS_RATES_2025.healthAdditional)) / 100;
  
  // Care insurance (Pflegeversicherung) - complex calculation:
  // Base rate: 1.8%
  // + 0.6% if childless and age >= 23
  // - Reductions for 2-5+ children (0.25% per child from 2nd to 5th)
  let careRate = SS_RATES_2025.care;
  
  if (dependents === 0 && age >= 23) {
    // Childless surcharge
    careRate += SS_RATES_2025.careChildless; // Total: 2.4%
  } else if (dependents >= 2) {
    // Reduction for multiple children (only for children under 25)
    const childIndex = Math.min(dependents, 5);
    careRate -= SS_RATES_2025.careMultiChild[childIndex - 1] || 0;
    careRate = Math.max(0.9, careRate); // Minimum 0.9% (half of base rate minus max reduction)
  }
  
  const careContrib = (healthBase * careRate) / 100;

  const socialSecurity = pensionContrib + unemploymentContrib + healthContrib + careContrib;

  // ============================================================================
  // INCOME TAX (Einkommensteuer) 2025
  // ============================================================================

  // Deductible expenses (Werbungskostenpauschale + deductible SS)
  const werbungskosten = 1230; // Standard deduction for employees 2025
  
  // Pension contributions are ~100% deductible from 2025
  const deductiblePension = pensionContrib;
  
  // Health and care insurance contributions (Basiskrankenversicherung) are deductible
  const deductibleHealth = healthContrib * 0.96; // ~96% for basic health coverage
  const deductibleCare = careContrib;
  
  // Total Sonderausgaben (special expenses)
  const sonderausgaben = deductiblePension + deductibleHealth + deductibleCare;

  // Personal allowance (Grundfreibetrag)
  let allowance = familyStatus === "married" 
    ? config.marriedAllowance 
    : config.personalAllowance;

  // Child allowance (Kinderfreibetrag) - 6.612€ per child for 2025
  // Applied only if more beneficial than Kindergeld (250€/month per child)
  // For high earners, Kinderfreibetrag is usually better
  const kinderfreibetragPerChild = 6612;
  const kindergeldPerYear = 250 * 12; // 3.000€ per child
  
  // For simplicity, we use Kinderfreibetrag if gross > 60.000€
  // (actual calculation would compare tax savings)
  if (grossIncome > 60000) {
    for (let i = 0; i < dependents; i++) {
      allowance += kinderfreibetragPerChild;
    }
  }

  // Taxable income (zu versteuerndes Einkommen)
  const taxableIncome = Math.max(0, grossIncome - werbungskosten - sonderausgaben - allowance);

  // Calculate tax using German progressive formula
  let incomeTax = 0;
  
  if (familyStatus === "married") {
    // Ehegattensplitting: split income, calculate tax, double it
    const splitIncome = taxableIncome / 2;
    incomeTax = calculateGermanIncomeTax(splitIncome) * 2;
  } else {
    incomeTax = calculateGermanIncomeTax(taxableIncome);
  }

  // Solidaritätszuschlag (5.5% of income tax)
  // Since 2021, only applies if income tax exceeds threshold
  // 2025: ~19.950€ for singles, ~39.900€ for married
  let soli = 0;
  const soliThreshold = familyStatus === "married" ? 39900 : 19950;
  if (incomeTax > soliThreshold) {
    soli = incomeTax * 0.055;
  }

  // Church tax (Kirchensteuer) - optional
  let churchTax = 0;
  if (hasChurchTax) {
    churchTax = incomeTax * (churchTaxRate / 100);
  }

  const totalTax = incomeTax + soli + churchTax;
  const taxRate = grossIncome > 0 ? (totalTax / grossIncome) * 100 : 0;

  // ============================================================================
  // BUILD BREAKDOWN with German labels
  // ============================================================================
  const breakdown: DeductionBreakdown[] = [];

  // Income Tax (Einkommensteuer)
  if (incomeTax > 0) {
    breakdown.push({
      id: "einkommensteuer",
      amount: incomeTax,
      rate: grossIncome > 0 ? (incomeTax / grossIncome) * 100 : 0,
      type: "tax",
    });
  }

  // Solidarity surcharge (Solidaritätszuschlag)
  if (soli > 0) {
    breakdown.push({
      id: "soli",
      amount: soli,
      rate: grossIncome > 0 ? (soli / grossIncome) * 100 : 0,
      type: "tax",
    });
  }

  // Church tax (Kirchensteuer)
  if (churchTax > 0) {
    breakdown.push({
      id: "kirchensteuer",
      amount: churchTax,
      rate: grossIncome > 0 ? (churchTax / grossIncome) * 100 : 0,
      type: "tax",
    });
  }

  // Social Security breakdown (Sozialversicherung)
  breakdown.push({
    id: "rentenversicherung",
    amount: pensionContrib,
    rate: grossIncome > 0 ? (pensionContrib / grossIncome) * 100 : 0,
    type: "social",
  });

  breakdown.push({
    id: "arbeitslosenversicherung",
    amount: unemploymentContrib,
    rate: grossIncome > 0 ? (unemploymentContrib / grossIncome) * 100 : 0,
    type: "social",
  });

  breakdown.push({
    id: "krankenversicherung",
    amount: healthContrib,
    rate: grossIncome > 0 ? (healthContrib / grossIncome) * 100 : 0,
    type: "social",
  });

  breakdown.push({
    id: "pflegeversicherung",
    amount: careContrib,
    rate: grossIncome > 0 ? (careContrib / grossIncome) * 100 : 0,
    type: "social",
  });

  return {
    tax: totalTax,
    taxRate,
    socialSecurity,
    allowances: allowance + werbungskosten + sonderausgaben,
    breakdown,
  };
}

/**
 * Calculate German income tax using the official 2025 formula
 * Source: §32a EStG
 * 
 * The German tax formula uses polynomial functions for the progressive zones,
 * not simple bracket rates. This is an accurate implementation.
 * 
 * @param zvE zu versteuerndes Einkommen (taxable income)
 */
function calculateGermanIncomeTax(zvE: number): number {
  // 2025 parameters (from Einkommensteuergesetz)
  const grundfreibetrag = 12096;
  
  if (zvE <= grundfreibetrag) {
    // Zone 0: No tax below Grundfreibetrag
    return 0;
  }
  
  if (zvE <= 17443) {
    // Zone 1: Progressive from 14% to ~24%
    // Formula: (1.088,67 * y + 1.400) * y
    // where y = (zvE - 12.096) / 10.000
    const y = (zvE - 12096) / 10000;
    return Math.floor((1088.67 * y + 1400) * y);
  }
  
  if (zvE <= 68429) {
    // Zone 2: Progressive from ~24% to 42%
    // Formula: (206,43 * z + 2.397) * z + 938,24
    // where z = (zvE - 17.443) / 10.000
    const z = (zvE - 17443) / 10000;
    return Math.floor((206.43 * z + 2397) * z + 938.24);
  }
  
  if (zvE <= 277825) {
    // Zone 3: Flat 42%
    // Formula: 0,42 * zvE - 10.636,31
    return Math.floor(0.42 * zvE - 10636.31);
  }
  
  // Zone 4: Flat 45% (Reichensteuer)
  // Formula: 0,45 * zvE - 18.971,06
  return Math.floor(0.45 * zvE - 18971.06);
}
